import { ApiKeyScopeEnum, IApiKey } from '@novu/shared';
import { useState } from 'react';
import { RiDeleteBin2Line, RiEyeLine, RiEyeOffLine } from 'react-icons/ri';
import { ConfirmationModal } from '@/components/confirmation-modal';
import { Button } from '@/components/primitives/button';
import { Card, CardContent, CardHeader } from '@/components/primitives/card';
import { CopyButton } from '@/components/primitives/copy-button';
import { Input } from '@/components/primitives/input';
import { Skeleton } from '@/components/primitives/skeleton';
import { showErrorToast, showSuccessToast } from '@/components/primitives/sonner-helpers';
import { useCreateScopedApiKey, useRevokeScopedApiKey } from '@/hooks/use-fetch-api-keys';

type ClientApiKeysCardProps = {
  apiKeys: IApiKey[];
  isLoading: boolean;
  canManage: boolean;
};

/**
 * Trigger-only keys handed to client systems. They can trigger workflows and manage subscribers
 * and topics, and are rejected by every other endpoint.
 */
export function ClientApiKeysCard({ apiKeys, isLoading, canManage }: ClientApiKeysCardProps) {
  const [name, setName] = useState('');
  const [keyToRevoke, setKeyToRevoke] = useState<IApiKey | null>(null);
  const createKey = useCreateScopedApiKey();
  const revokeKey = useRevokeScopedApiKey();
  const clientKeys = apiKeys.filter((apiKey) => apiKey.scope === ApiKeyScopeEnum.TRIGGER);

  const handleCreate = async () => {
    try {
      await createKey.mutateAsync(name.trim());
      setName('');
      showSuccessToast('Client key created');
    } catch (e: unknown) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to create client key');
    }
  };

  const handleRevoke = async () => {
    if (!keyToRevoke?._id) return;

    try {
      await revokeKey.mutateAsync(keyToRevoke._id);
      setKeyToRevoke(null);
      showSuccessToast('Client key revoked');
    } catch (e: unknown) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to revoke client key');
    }
  };

  return (
    <>
      <Card className="w-full overflow-hidden shadow-none">
        <CardHeader>
          Client Keys
          <p className="text-foreground-500 mt-1 text-xs font-normal">
            Trigger-only keys for client systems: they can trigger workflows and manage subscribers and topics, nothing
            else. Create a new key before revoking the old one to rotate without downtime; a revoked key stops working
            within a minute.
          </p>
        </CardHeader>
        <CardContent className="rounded-b-xl border-t bg-white p-4">
          <div className="space-y-4">
            {isLoading && <Skeleton className="h-[38px] w-full rounded-lg" />}
            {!isLoading && clientKeys.length === 0 && (
              <p className="text-foreground-500 text-xs">No client keys in this environment yet.</p>
            )}
            {clientKeys.map((apiKey) => (
              <ClientKeyRow
                key={apiKey._id ?? apiKey.key}
                apiKey={apiKey}
                canRevoke={canManage}
                onRevoke={() => setKeyToRevoke(apiKey)}
              />
            ))}
            {canManage && (
              <div className="flex items-center gap-2 border-t pt-4">
                <Input
                  placeholder="Key name, e.g. Finnet production"
                  value={name}
                  maxLength={64}
                  onChange={(event) => setName(event.target.value)}
                />
                <Button
                  size="sm"
                  variant="secondary"
                  mode="outline"
                  onClick={handleCreate}
                  disabled={!name.trim()}
                  isLoading={createKey.isPending}
                >
                  Create key
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      <ConfirmationModal
        open={!!keyToRevoke}
        onOpenChange={(open) => !open && setKeyToRevoke(null)}
        onConfirm={handleRevoke}
        title="Revoke client key"
        description={`Systems using "${keyToRevoke?.name ?? 'this key'}" will be rejected within a minute. This cannot be undone.`}
        confirmButtonText="Revoke key"
        confirmButtonVariant="error"
        isLoading={revokeKey.isPending}
      />
    </>
  );
}

function ClientKeyRow({ apiKey, canRevoke, onRevoke }: { apiKey: IApiKey; canRevoke: boolean; onRevoke: () => void }) {
  const [showSecret, setShowSecret] = useState(false);
  const masked = `${'•'.repeat(28)}${apiKey.key.slice(-4)}`;

  return (
    <div className="grid grid-cols-[1fr_400px] items-center gap-3">
      <div className="flex flex-col">
        <span className="text-foreground-600 text-xs font-medium">{apiKey.name ?? 'Client key'}</span>
        {apiKey.createdAt && (
          <span className="text-foreground-400 text-2xs">
            Created {new Date(apiKey.createdAt).toLocaleDateString()}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Input
          className="cursor-default font-mono text-neutral-500!"
          value={showSecret ? apiKey.key : masked}
          readOnly
          trailingNode={<CopyButton valueToCopy={apiKey.key} />}
          inlineTrailingNode={
            <button type="button" onClick={() => setShowSecret(!showSecret)}>
              {showSecret ? <RiEyeOffLine className="text-text-sub" /> : <RiEyeLine className="text-text-sub" />}
            </button>
          }
        />
        {canRevoke && (
          <Button
            size="md"
            variant="error"
            mode="outline"
            onClick={onRevoke}
            className="h-[38px] min-w-[38px] p-0"
            aria-label="Revoke key"
          >
            <RiDeleteBin2Line className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
